package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.repository.AiScheduleRepository
import com.evoapps.evoshift.domain.schedule.ai.AiAssignException
import com.evoapps.evoshift.domain.schedule.ai.AiAssignFailure
import com.evoapps.evoshift.domain.schedule.ai.AiAssignStatus
import com.evoapps.evoshift.domain.schedule.ai.AiScheduleAnswer
import com.evoapps.evoshift.domain.schedule.ai.AiScheduleJson
import com.evoapps.evoshift.domain.schedule.ai.AiScheduleRequest
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlin.coroutines.cancellation.CancellationException

/**
 * The aiAssignStatus and suggestAssignmentsWithAI callables (functions/aiAssign.js, SH-90). The
 * request is the pseudonymised problem exactly as [AiScheduleRequest] serialises; the workplace
 * id goes beside it for the server's permission, plan and quota checks and is never shown to the
 * model. Refusals are mapped from the HttpsError code and `details.reason`, never the message.
 */
class AiScheduleRepositoryImpl(private val functions: CloudFunctionsClient) : AiScheduleRepository {

    override suspend fun status(workplaceId: String): Result<AiAssignStatus> = guarded {
        val o = functions.call("aiAssignStatus", buildJsonObject { put("workplaceId", workplaceId) }) as? JsonObject
        AiAssignStatus(
            enabled = o?.get("enabled")?.jsonPrimitive?.booleanOrNull ?: false,
            remainingToday = o?.get("remainingToday")?.jsonPrimitive?.intOrNull ?: 0
        )
    }

    override suspend fun suggest(workplaceId: String, request: AiScheduleRequest): Result<AiScheduleAnswer> = guarded {
        val result = functions.call("suggestAssignmentsWithAI", buildJsonObject {
            put("workplaceId", workplaceId)
            put("problem", AiScheduleJson.encodeToJsonElement(AiScheduleRequest.serializer(), request))
        })
        runCatching { AiScheduleJson.decodeFromJsonElement(AiScheduleAnswer.serializer(), result) }
            .getOrElse { throw AiAssignException(AiAssignFailure.MODEL_FAILED, it) }
    }

    private inline fun <T> guarded(block: () -> T): Result<T> = try {
        Result.success(block())
    } catch (e: CancellationException) {
        throw e
    } catch (e: AiAssignException) {
        Result.failure(e)
    } catch (e: CloudFunctionsException) {
        Result.failure(AiAssignException(failureOf(e), e))
    } catch (e: Exception) {
        Result.failure(AiAssignException(AiAssignFailure.UNKNOWN, e))
    }

    companion object {
        /** `details.reason` first (functions/aiAssign.js REASONS), else the error code. */
        fun failureOf(e: CloudFunctionsException): AiAssignFailure {
            val reason = (e.details as? JsonObject)?.get("reason")?.jsonPrimitive?.contentOrNull
            return when (reason) {
                "DISABLED", "NOT_CONFIGURED" -> AiAssignFailure.DISABLED
                "PLAN_REQUIRED" -> AiAssignFailure.PLAN_REQUIRED
                "NOT_MANAGER", "NOT_SIGNED_IN" -> AiAssignFailure.NOT_ALLOWED
                "QUOTA_EXCEEDED" -> AiAssignFailure.QUOTA_EXCEEDED
                "TOO_LARGE" -> AiAssignFailure.TOO_LARGE
                "INVALID_PAYLOAD" -> AiAssignFailure.INVALID_REQUEST
                "MODEL_FAILED" -> AiAssignFailure.MODEL_FAILED
                else -> when (e.code) {
                    "unauthenticated", "permission-denied" -> AiAssignFailure.NOT_ALLOWED
                    "resource-exhausted" -> AiAssignFailure.QUOTA_EXCEEDED
                    "invalid-argument" -> AiAssignFailure.INVALID_REQUEST
                    "failed-precondition" -> AiAssignFailure.PLAN_REQUIRED
                    "unavailable", "deadline-exceeded" -> AiAssignFailure.MODEL_FAILED
                    else -> AiAssignFailure.UNKNOWN
                }
            }
        }
    }
}
